<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { useScroll } from '@vueuse/core'
import { t } from '@/i18n/utils'

const props = defineProps<{
  value: boolean
  chapter: any
  chapterNumber: number
  totalChapters: number
  theme: { background: string; size: string; skinId?: string }
  backgrounds: string[]
  fontMap: string[]
  skins?: { id: string; thumb: string; background: string }[]
  sizeLabel: string
  decreaseLabel: string
  increaseLabel: string
  prevLabel: string
  nextLabel: string
}>()
const emit = defineEmits<{
  (event: 'input', value: boolean): void
  (event: 'prev-chapter'): void
  (event: 'next-chapter'): void
  (event: 'open-toc'): void
  (event: 'change-size', direction: number): void
  (event: 'change-background', color: string): void
  (event: 'change-skin', id: string): void
}>()
const section = ref<'size' | 'theme' | null>(null)
watch(() => props.value, (value) => {
  if (!value)
    section.value = null
})
const container = inject<Ref<HTMLElement | undefined>>('containerRef', ref())
const { y } = useScroll(container)
const progress = computed(() => {
  const top = y.value
  if (!props.value || !container.value)
    return 0
  const el = container.value
  // Scroll mode contains several chapters: measure the currently visible chapter only.
  const chapterEl = [...el.querySelectorAll<HTMLElement>('[data-chapter-id]')].find(e => e.dataset.chapterId === props.chapter?.id)
  const start = chapterEl ? chapterEl.getBoundingClientRect().top - el.getBoundingClientRect().top + top : 0
  const height = chapterEl?.offsetHeight || el.scrollHeight
  return Math.round(Math.min(1, Math.max(0, (top - start) / Math.max(1, height - el.clientHeight))) * 100)
})
const dark = computed(() => props.theme.background === '#292929')
const hasPrev = computed(() => !!(props.chapter?.prev || props.chapter?.prevId))
function toggle(target: 'size' | 'theme') {
  section.value = section.value === target ? null : target
}
</script>

<template>
  <BasicPopup :value="value" :mask="false" @input="emit('input', $event)">
    <div class="sprig-reader-menu" :class="{ dark }">
      <div v-if="section === 'size'" class="size-panel">
        <span>{{ sizeLabel }}</span>
        <div class="size-controls">
          <button type="button" :disabled="theme.size === fontMap[0]" @click="emit('change-size', -1)">
            {{ decreaseLabel }}
          </button>
          <output>{{ theme.size }}</output>
          <button type="button" :disabled="theme.size === fontMap[fontMap.length - 1]" @click="emit('change-size', 1)">
            {{ increaseLabel }}
          </button>
        </div>
      </div>
      <div v-if="section === 'theme'" class="theme-panel" :aria-label="t('NovelSprigTheme')">
        <button
          v-for="skin in skins || []" :key="skin.id" type="button" class="theme-swatch"
          :class="{ selected: theme.skinId === skin.id }" :style="{ background: skin.background }"
          :aria-label="skin.id" :aria-pressed="theme.skinId === skin.id" @click="emit('change-skin', skin.id)"
        >
          <img :src="skin.thumb" alt="">
        </button>
        <button
          v-for="color in backgrounds" :key="color" type="button" class="theme-swatch"
          :class="{ selected: !theme.skinId && theme.background === color }" :style="{ background: color }"
          :aria-label="`${t('NovelSprigTheme')} ${color}`" :aria-pressed="!theme.skinId && theme.background === color"
          @click="emit('change-background', color)"
        />
      </div>
      <div class="chapter-progress">
        <div class="chapter-label">
          <button type="button" class="previous" :disabled="!hasPrev" :aria-label="prevLabel" @click="emit('prev-chapter')">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m10 4-4 4 4 4" /></svg>
          </button>
          <span>{{ t('NovelSprigChapterCount', { current: chapterNumber, total: totalChapters }) }}</span>
        </div>
        <span>{{ progress }}%</span>
      </div>
      <div class="progress-track" role="progressbar" :aria-label="t('NovelSprigChapterProgress')" :aria-valuenow="progress" aria-valuemin="0" aria-valuemax="100">
        <div :style="{ width: `${progress}%` }" />
      </div>
      <div class="menu-actions">
        <button type="button" @click.stop="emit('open-toc')">
          ☷ {{ t('ChapterList') }}
        </button>
        <button type="button" :aria-label="sizeLabel" :aria-expanded="section === 'size'" @click="toggle('size')">
          Aa
        </button>
        <button type="button" :aria-expanded="section === 'theme'" @click="toggle('theme')">
          ◐ {{ t('NovelSprigTheme') }}
        </button>
        <button type="button" @click="emit('next-chapter')">
          {{ nextLabel }} ›
        </button>
      </div>
    </div>
  </BasicPopup>
</template>

<style scoped>
.sprig-reader-menu { padding: 16px 20px calc(20px + env(safe-area-inset-bottom, 0px)); background: #FFFFFF; color: #1F2937; border-radius: 0; box-shadow: 0 -6px 24px #1F29370A; --pill-bg: #D1FAE5; --pill-color: #10B981; --track-bg: #A7F3D0; }
.dark { background: #292929; color: #D1FAE5; --pill-bg: #FFFFFF14; --pill-color: #A7F3D0; --track-bg: #FFFFFF26; }
button { font: inherit; border: 0; cursor: pointer; color: var(--pill-color); background: var(--pill-bg); }
button:disabled { opacity: .4; cursor: default; }
button:focus-visible { outline: 2px solid var(--pill-color); outline-offset: 3px; }
.chapter-progress { display: flex; align-items: center; justify-content: space-between; font-size: 12px; line-height: 20px; }
.chapter-label { display: flex; align-items: center; }
.previous { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; background: none; width: 18px; height: 20px; padding: 0; margin-left: -5px; }
.previous svg { display: block; width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.progress-track { margin-top: 10px; height: 3px; background: var(--track-bg); overflow: hidden; }
.progress-track > div { height: 100%; background: #10B981; }
.menu-actions { display: flex; align-items: center; justify-content: space-between; margin-top: 14px; }
.menu-actions button { min-height: 34px; padding: 7px 12px; border-radius: 22px; font-size: 12px; white-space: nowrap; }
.size-panel { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; font-size: 13px; }
.size-controls { display: flex; align-items: center; border-radius: 20px; background: var(--pill-bg); overflow: hidden; }
.size-controls button { min-width: 50px; height: 36px; }
.size-controls output { min-width: 40px; text-align: center; }
.theme-panel { display: flex; align-items: center; justify-content: space-around; margin-bottom: 18px; }
.theme-swatch { width: 28px; height: 28px; border: 1px solid #A7F3D0; border-radius: 50%; padding: 0; overflow: hidden; }
.theme-swatch img { width: 100%; height: 100%; object-fit: cover; }
.theme-swatch.selected { outline: 2px solid var(--pill-color); outline-offset: 2px; }
</style>
